// Utility layer (TS-2.5.04) - pure, no side effects. Builds the project form's product picker
// options: every ACTIVE product, plus - if and only if this project already carries one - the
// retired product it currently names (D-268: a project keeps a retired product until the
// assignment is CHANGED, so the picker must not silently drop the option the project already
// holds, which would make the form lie about what it carries). Labeling ("(retired)") is left to
// the caller (ProjectForm.tsx) via `isRetired`, since this file has no strings dependency, the same
// split projectDraft.ts/projectSort.ts already keep.
import type { ProductRecord } from "../../../api/generated/contracts";

export interface ProductOption {
  readonly key: string;
  readonly label: string;
  readonly isRetired: boolean;
  // Carried through for D-279's inheritance display (ProjectForm.tsx's Business owner field):
  // the picker already resolves a product's NAME for the currently assigned key, and the
  // inheritance description needs that same product's businessOwner alongside it - reading it
  // straight off the option this file already builds avoids a second products lookup at the
  // call site.
  readonly businessOwner: string | null;
}

export function buildProductOptions(
  products: readonly ProductRecord[],
  currentProductKey: string | null,
): readonly ProductOption[] {
  const active = products.filter((product): product is ProductRecord & { key: string } => Boolean(product.key) && product.isActive);

  const options: ProductOption[] = active.map((product) => ({
    key: product.key,
    label: product.name,
    isRetired: false,
    businessOwner: product.businessOwner,
  }));

  const alreadyIncluded = options.some((option) => option.key === currentProductKey);
  if (currentProductKey && !alreadyIncluded) {
    const current = products.find((product) => product.key === currentProductKey);
    options.push({
      key: currentProductKey,
      label: current?.name ?? currentProductKey,
      isRetired: true,
      businessOwner: current?.businessOwner ?? null,
    });
  }

  return [...options].sort((a, b) => a.label.localeCompare(b.label));
}

/** The label a read-only view or a picker's current selection should show for a product key. */
export function labelForProductKey(options: readonly ProductOption[], productKey: string | null): string | null {
  if (!productKey) return null;
  return options.find((option) => option.key === productKey)?.label ?? null;
}

/** The assigned product's OWN business owner (D-279's inheritance source), or null when there is
 *  no assigned product or that product names no business owner - the caller decides how to
 *  render "no product" versus "product has no business owner", since those read differently. */
export function businessOwnerForProductKey(options: readonly ProductOption[], productKey: string | null): string | null {
  if (!productKey) return null;
  return options.find((option) => option.key === productKey)?.businessOwner ?? null;
}
